import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";
import TrendChartBinSizeDropdown from "./TrendChartBinSizeDropdown";
import TrendChart from "../Dashboard/TrendChart/TrendChart";
import PnlSummaryChart from "../Dashboard/PnlSummaryChart/PnlSummaryChart";
import styled, { css } from "styled-components/macro";
import {
  useTrendsPageFilterState,
  useFocusRange,
  useDisplayTrendsPageTimeline,
} from "utils/customHooks/genericHooks";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import Timelines from "components/Dashboard/Timeline/Timelines/Timelines";
import ShowTimelineButton from "./ShowTimelineButton";
import { useLocalStorageState } from "utils/useLocalStorageState";
import { HighlightTagsSelector } from "components/Dashboard/HubletView/SingleTimeline/HighlightTagsSelector/HighlightTagsSelector";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useTrendChartBarRangesForBarstacks } from "utils/customHooks/trendChartHooks";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import { TRENDS_CARD_PROPS } from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import TrendChartCardControls from "components/Dashboard/TrendChart/TrendChartCardControls/TrendChartCardControls";
import {
  TREND_CHART_CARD_ID,
  useIsTrendChartExpandedTrendsPage,
} from "pages/DashboardPages/dashboardPagesUtils";
import OverallBarstack from "components/Dashboard/OverallBarstack/OverallBarstack";
import GroupMetrics from "components/Dashboard/GroupMetrics/GroupMetrics";
import PnlChartCardControls from "components/Dashboard/PnlSummaryChart/PnlChartCardControls";
import TimelineCardControls from "components/Dashboard/Timeline/TimelineCardControls/TimelineCardControls";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import MachineBreakdownChart from "components/Dashboard/MachineBreakdownChart/MachineBreakdownChart";

export const ButterflyAndMetricsSectionCollapsible = () => {
  const { translate } = useFlightTranslation();
  const [isButterflyGroupMetricsExpanded, setIsButterflyGroupMetricsExpanded] =
    useLocalStorageState("collapsibles:trendsPage:butterfly", true);
  const [flightConfig] = useFlightConfig();
  return (
    <CardWithControls
      {...TRENDS_CARD_PROPS}
      title={translate("Summary")}
      id={TREND_CHART_CARD_ID}
      className={`Card_Butterfly${
        isButterflyGroupMetricsExpanded ? " break-after expanded" : ""
      } Card_Butterfly_TrendsPage`}
      customCss={css`
        .cardContent {
          padding-top: 28px;
          padding-bottom: 32px;
        }
        [class*="GroupMetricsStyles"] {
          padding-bottom: 0;
        }
      `}
      CardConfigControls={<TrendChartCardControls />}
      onCollapse={nextExpanded =>
        setIsButterflyGroupMetricsExpanded(nextExpanded)
      }
    >
      {flightConfig.general.trendsPageEnableGroupMetrics && (
        <div style={{ marginTop: -20 }}>
          <GroupMetrics />
        </div>
      )}
      <OverallBarstack />
    </CardWithControls>
  );
};

export const TrendChartSectionCollapsible = () => {
  const [isTrendChartExpanded, setIsTrendChartExpanded] =
    useIsTrendChartExpandedTrendsPage();

  const { translate } = useFlightTranslation();
  const trendChartHeight = 500;
  const isPrint = useMediaQuery("print");
  return (
    <CardWithControls
      {...TRENDS_CARD_PROPS}
      title={translate("Trends")}
      id={TREND_CHART_CARD_ID}
      className={`Card_TrendChart${
        isTrendChartExpanded ? " break-after expanded" : ""
      } Card_TrendChart_TrendsPage`}
      customCss={css`
        .cardContent {
          overflow: ${isPrint ? "visible" : "hidden"};
          margin-top: 10px;
          padding-top: 24px;
        }
      `}
      CardConfigControls={<TrendChartCardControls />}
      onCollapse={nextExpanded => setIsTrendChartExpanded(nextExpanded)}
    >
      <TrendChartBinSizeDropdown />
      <StyledDiv style={{ height: trendChartHeight }}>
        <TrendChart />
      </StyledDiv>
    </CardWithControls>
  );
};
const StyledDiv = styled.div`
  padding: 10px;
  box-sizing: border-box;
`;

export const PnlBreakdownSectionCollapsible = () => {
  const { isPnlBreakdownChartVisible } = useSettings();

  const [isPnlChartExpanded, setIsPnlChartExpanded] = useLocalStorageState(
    "collapsibles:trendsPage:pnlBreakdownChart",
    true
  );
  const { translate } = useFlightTranslation();
  const isPrint = useMediaQuery("print");
  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();
  const isManyBars = trendChartBarsRanges.length > 12;
  return isPnlBreakdownChartVisible ? (
    <CardWithControls
      {...TRENDS_CARD_PROPS}
      title={translate("Performance & Loss Breakdown")}
      id={"Card_PnlChart_TrendsPage"}
      className={`Card_TrendChart${
        isPnlChartExpanded ? " break-after expanded" : ""
      } Card_PnlChart Card_PnlChart_TrendsPage ${
        isManyBars && isPrint ? "manyTrendBars" : ""
      }`}
      customCss={css`
        .cardContent {
          margin-top: 10px;
          padding-top: 36px;
          padding-bottom: 36px;
        }
      `}
      onCollapse={nextExpanded => setIsPnlChartExpanded(nextExpanded)}
      CardConfigControls={<PnlChartCardControls />}
    >
      <PnlSummaryChart />
    </CardWithControls>
  ) : null;
};

export const TimelineSection = () => {
  const [
    {
      general: { trendsPageShowTagHighlight },
    },
  ] = useFlightConfig();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const focusRange = useFocusRange();
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [showTimeline, setShowTimeline] = useDisplayTrendsPageTimeline();
  const { translate } = useFlightTranslation();
  // hide timeline again when focus range or filter state changes
  useEffect(() => {
    const shouldHide =
      focusRange && focusRange.lt - focusRange.ge > 10 * ONE_DAY_OF_MS;
    if (shouldHide) {
      setShowTimeline(false);
    }
  }, [focusRange, trendsPageFilterState, setShowTimeline]);
  const isPrint = useMediaQuery("print");
  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();
  const isManyBars = trendChartBarsRanges.length > 12;
  return (
    <CardWithControls
      {...TRENDS_CARD_PROPS}
      title={translate("Timelines")}
      className={`Card_Timeline${
        isTimelineExpanded ? " break-after expanded" : ""
      } Card_Timeline Card_Timeline_TrendsPage ${
        isManyBars && isPrint ? "manyTrendBars" : ""
      }`}
      customCss={css`
        overflow: visible;
        .cardContent {
          margin-top: 10px;
          padding-top: 36px;
          padding-bottom: 36px;
        }
        overflow: visible;
      `}
      onCollapse={nextExpanded => setIsTimelineExpanded(nextExpanded)}
      CardConfigControls={<TimelineCardControls />}
    >
      {showTimeline ? (
        <div
          className={`cardItem cardPadding timelineCard Card_Timeline Card_Timeline_TrendsPage ${
            isManyBars && isPrint ? "manyTrendBars" : ""
          }`}
          // need overflow for the timeline hover tooltip to show up
          style={{ overflow: "visible" }}
        >
          <Timelines />
          {trendsPageShowTagHighlight ? <HighlightTagsSelector /> : null}
        </div>
      ) : (
        <ShowTimelineButton
          onClick={() => setShowTimeline(true)}
          showNote={true}
        />
      )}
    </CardWithControls>
  );
};

export const MachineBreakdownSectionCollapsible = () => {
  const { translate } = useFlightTranslation();
  const [isMachineBreakdownExpanded, setIsMachineBreakdownExpanded] =
    useLocalStorageState("collapsibles:trendsPage:machineBreakdown", true);

  // Check if a process is selected in the filter state
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const isProcessSelected = trendsPageFilterState.process.name !== "All";

  // Don't render the machine breakdown chart when a process is selected
  if (isProcessSelected) {
    return null;
  }

  return (
    <CardWithControls
      {...TRENDS_CARD_PROPS}
      title={translate("Machine Breakdown")}
      id={"Card_MachineBreakdown_TrendsPage"}
      className={`Card_MachineBreakdown${
        isMachineBreakdownExpanded ? " break-after expanded" : ""
      } Card_MachineBreakdown Card_MachineBreakdown_TrendsPage`}
      customCss={css`
        .cardContent {
          margin-top: 10px;
          padding-top: 36px;
          padding-bottom: 36px;
        }
        .horizontalBarstackWrapper {
          background: none;
        }
      `}
      onCollapse={nextExpanded => setIsMachineBreakdownExpanded(nextExpanded)}
      CardConfigControls={<MachineBreakdownCardControls />}
    >
      <MachineBreakdownChart />
    </CardWithControls>
  );
};

const MachineBreakdownCardControls = () => {
  return null;
};
